import React, { useEffect, useMemo, useState } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import { GridColDef } from '@mui/x-data-grid';
import { usersApi, DbUser } from '../api/client';
import { PageHeader } from './layout/PageHeader';
import { PageLoader } from './PageLoader';
import { StatCard } from './layout/StatCard';
import { TableCard } from './layout/TableCard';
import { ResizableDataGrid } from './grid/ResizableDataGrid';
import { ChartCard } from './charts/ChartCard';
import { NexusBarChart, NexusDonut } from './charts/NexusCharts';
import { tokens } from '../theme/tokens';

const TeamManagement: React.FC = () => {
  const [search, setSearch] = useState('');

  const [topPanel, setTopPanel] = useState<null | 'dbTotal' | 'dbAdmins' | 'dbManagers' | 'dbIcs'>(null);
  const [showAllPanel, setShowAllPanel] = useState(false);

  const [dbUsers, setDbUsers] = useState<DbUser[]>([]);
  const [loadingDb, setLoadingDb] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshDb = async () => {
    setLoadingDb(true);
    setError(null);
    try {
      const res = await usersApi.getAll({ scopePage: 'team' });
      setDbUsers(res.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load Nexus team');
    } finally {
      setLoadingDb(false);
    }
  };

  useEffect(() => {
    refreshDb();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dbSummary = useMemo(() => {
    const total = dbUsers.length;
    const admins = dbUsers.filter((u) => String(u.role || '').toUpperCase() === 'ADMIN' || (u.adGroups || u.cognitoGroups || []).includes('Admin')).length;
    const managers = dbUsers.filter((u) => String(u.role || '').toUpperCase().includes('MANAGER')).length;
    return { total, admins, managers, members: Math.max(0, total - admins - managers) };
  }, [dbUsers]);

  const dbRoleMix = useMemo(() => {
    const admins = dbSummary.admins;
    const managers = dbSummary.managers;
    const ics = dbSummary.members;
    return [
      { name: 'Admins', value: admins },
      { name: 'Managers', value: managers },
      { name: 'ICs', value: ics },
    ];
  }, [dbSummary.admins, dbSummary.managers, dbSummary.members]);

  const dbDeptTop = useMemo(() => {
    const map = new Map<string, number>();
    (dbUsers || []).forEach((u: any) => {
      const d = String(u.department || '—');
      map.set(d, (map.get(d) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [dbUsers]);

  const filteredDb = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return dbUsers;
    return dbUsers.filter((u) => {
      const name = (u.displayName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      const title = (u.jobTitle || '').toLowerCase();
      const dept = (u.department || '').toLowerCase();
      return name.includes(q) || email.includes(q) || title.includes(q) || dept.includes(q);
    });
  }, [dbUsers, search]);

  const dbCols: GridColDef[] = [
    { field: 'displayName', headerName: 'Name', flex: 1, minWidth: 220, valueGetter: (p) => p.row.displayName || `${p.row.firstName || ''} ${p.row.lastName || ''}`.trim() || p.row.email },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 240, valueGetter: (p) => p.row.email || '—' },
    { field: 'role', headerName: 'Role', width: 160, valueGetter: (p) => p.row.role || '—' },
    { field: 'department', headerName: 'Dept', width: 160, valueGetter: (p) => p.row.department || '—' },
    { field: 'jobTitle', headerName: 'Title', flex: 1, minWidth: 220, valueGetter: (p) => p.row.jobTitle || '—' },
  ];

  // Directory integrations removed

  return (
    <PageLoader loading={loadingDb}>
      <Container maxWidth="xl" sx={{ pt: 1, pb: 3 }}>
      <PageHeader
        title="Team"
        subtitle="Nexus directory."
        actions={
          <>
            <TextField
              size="small"
              placeholder="Search team…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: { xs: '100%', sm: 420 } }}
            />
            <Button
              variant="outlined"
              onClick={() => refreshDb()}
              sx={{ borderRadius: 2 }}
            >
              Refresh
            </Button>
          </>
        }
      />

      {error ? (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <StatCard
                title="Members"
                value={dbSummary.total}
                subtitle="In database"
                accent={tokens.color.accent}
                onClick={() => {
                  setShowAllPanel(false);
                  setTopPanel((p) => (p === 'dbTotal' ? null : 'dbTotal'));
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatCard
                title="Admins"
                value={dbSummary.admins}
                subtitle="Platform admins"
                accent={tokens.color.accent}
                onClick={() => {
                  setShowAllPanel(false);
                  setTopPanel((p) => (p === 'dbAdmins' ? null : 'dbAdmins'));
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatCard
                title="Managers"
                value={dbSummary.managers}
                subtitle="PM/Managers"
                accent={tokens.color.success}
                onClick={() => {
                  setShowAllPanel(false);
                  setTopPanel((p) => (p === 'dbManagers' ? null : 'dbManagers'));
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatCard
                title="ICs"
                value={dbSummary.members}
                subtitle="Non-admin/manager"
                accent={tokens.color.warning}
                onClick={() => {
                  setShowAllPanel(false);
                  setTopPanel((p) => (p === 'dbIcs' ? null : 'dbIcs'));
                }}
              />
            </Grid>
          </Grid>

          <Collapse in={!!topPanel} unmountOnExit>
            <TableCard
              title={
                topPanel === 'dbTotal'
                  ? 'Nexus members'
                  : topPanel === 'dbAdmins'
                    ? 'Nexus admins'
                    : topPanel === 'dbManagers'
                      ? 'Nexus managers'
                      : 'Nexus ICs'
              }
              actions={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button size="small" variant="outlined" onClick={() => setShowAllPanel((s) => !s)} sx={{ borderRadius: 2 }}>
                    {showAllPanel ? 'Show less' : 'Show all'}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => setTopPanel(null)} sx={{ borderRadius: 2 }}>
                    Close
                  </Button>
                </Stack>
              }
            >
              {(() => {
                const list = dbUsers.filter((u) => {
                  const role = String(u.role || '').toUpperCase();
                  const isAdmin = role === 'ADMIN' || (u.adGroups || u.cognitoGroups || []).includes('Admin');
                  const isMgr = role.includes('MANAGER');
                  if (topPanel === 'dbTotal') return true;
                  if (topPanel === 'dbAdmins') return isAdmin;
                  if (topPanel === 'dbManagers') return isMgr;
                  if (topPanel === 'dbIcs') return !isAdmin && !isMgr;
                  return true;
                });

                if (!list.length) {
                  return (
                    <Alert severity="info" variant="outlined">
                      No results.
                    </Alert>
                  );
                }

                const shown = list.slice(0, showAllPanel ? 1000 : 5);
                return (
                  <Stack spacing={1}>
                    {shown.map((u: any) => {
                      const name = u.displayName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '—';
                      const secondary = `${u.email || '—'} • ${u.department || '—'} • ${u.jobTitle || '—'}`;
                      return (
                        <Box
                          key={u.id}
                          sx={{
                            p: 1,
                            borderRadius: `${tokens.radius.sm}px`,
                            border: `1px solid ${tokens.color.border}`,
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: tokens.color.surfaceMuted },
                          }}
                          onClick={() => {
                            setSearch(String(name));
                          }}
                        >
                          <Typography sx={{ fontWeight: 650 }} noWrap title={String(name)}>
                            {String(name)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap title={String(secondary)}>
                            {String(secondary)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                );
              })()}
            </TableCard>
          </Collapse>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <ChartCard title="Roles mix" subtitle="Admins / Managers / ICs" height={260}>
                <NexusDonut
                  height={200}
                  centerLabel="Members"
                  centerValue={String(dbSummary.total)}
                  data={dbRoleMix}
                />
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={8}>
              <ChartCard title="Top departments" subtitle="Nexus users by department" height={260}>
                <NexusBarChart
                  data={dbDeptTop}
                  xKey="name"
                  height={220}
                  series={[{ key: 'value', name: 'Members' }]}
                  valueFormatter={(v) => String(Math.round(v))}
                />
              </ChartCard>
            </Grid>
          </Grid>

          <TableCard
            title="Nexus directory"
            subtitle="Users stored in the Nexus database."
            actions={loadingDb ? <CircularProgress size={18} /> : null}
          >
            <div style={{ height: 600, width: '100%' }}>
              <ResizableDataGrid
                storageKey="gridWidths.team.nexus"
                enableDensityControls
                rows={filteredDb}
                columns={dbCols}
                getRowId={(r) => r.id}
                disableRowSelectionOnClick
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': { backgroundColor: tokens.color.surfaceMuted },
                  '& .MuiDataGrid-row:hover': { backgroundColor: tokens.color.surfaceMuted },
                }}
              />
            </div>
          </TableCard>
        </>
      </Container>
    </PageLoader>
  );
};

export default TeamManagement;
